import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import PropertyTypeDropdown from "../customHooks/propertyTypeDropdown";
import PropertyTypeDescriptionDropdown from "../customHooks/propertyTypeDescriptionDropdown";
import UseOfPropertyDropdown from "../customHooks/useOfPropertyDropdown";
import PurposeOfPropertyDropdown from "../customHooks/purposeOfPropertyDropdown";
import axiosInstance from "../../components/axiosInstance";

type FormData = {
  property_type_id: any;
  property_type_description_id: any;
  use_of_property_id: any;
  purpose_of_use_id: any;
  description_name: any;
};

const AddPropertyDescription: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    property_type_id: "",
    property_type_description_id: "",
    use_of_property_id: "",
    purpose_of_use_id: "",
    description_name: ""
  });

  const [errors, setErrors] = useState<any>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [dataExistMessage, setDataExistMessage] = useState<string>("");
  const [dataFailedMessage, setDataFailedMessage] = useState<string>("");

  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | any
  ) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData({
        ...formData,
        [name]: files ? files[0] : null,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      property_type_id: "",
      property_type_description_id: "",
      use_of_property_id: "",
      purpose_of_use_id: "",
      description_name: ""
    });
    setErrors({
      description_name: "",
    });
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (formData.description_name.trim() === "") {
      newErrors.description_name = "Description name is required";
    }

    if (!formData.property_type_id) newErrors.property_type_id = "Property Type is required";
    if (!formData.property_type_description_id) newErrors.property_type_description_id = "Property Type Description is required";
    if (!formData.use_of_property_id) newErrors.use_of_property_id = "Use Of Property is required";
    if (!formData.purpose_of_use_id) newErrors.purpose_of_use_id = "Purpose of property is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const response = await axiosInstance.post(`${API_URLS.insertPropertyDescription}`, formData);
      
        if (response.data.data.message) {
          resetForm();
          setDataExistMessage(`${response.data.data.message}`);
          setTimeout(() => {
            setDataExistMessage("");
          }, 5000);
        } else {
          resetForm();
          setSuccessMessage(`${response.data.message}`);
          setTimeout(() => {
            setSuccessMessage("");
          }, 5000);
        }

        setErrors({});
      } catch (error) {
        setDataFailedMessage("Property Description Add failed.");
        setTimeout(() => {
          setDataFailedMessage("");
        }, 5000);
      }
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (formRef.current && !formRef.current.contains(e.target as Node)) {
      setErrors({});
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState<any[]>([]);

  const removeTag = (tag: string) => {
    const newTags = tags.filter((t) => t !== tag);
    setTags(newTags);
    setFormData((prevData) => ({
      ...prevData,
      description_name: newTags.join(", ")
    }));
  };

  const addTag = (tag: string) => {
    if (tag && !tags.includes(tag)) {
      const newTags = [...tags, tag];
      setTags(newTags);
      setFormData((prevData) => ({
        ...prevData,
        description_name: newTags.join(", ")
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim() !== '') {
      addTag(inputValue.trim());
      setInputValue('');
      e.preventDefault();
    }
  };

  const handleBlur = () => {
    if (inputValue.trim() !== '') {
      addTag(inputValue.trim());
      setInputValue('');
    }
  };

  const [handleDropdownToggle, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown();
  const [handleDropdownTogglePropertyDescription, isDropdownOpenPropertyDescription, , propertyDescriptions, loadingPropertyDescription, setIsDropdownOpenPropertyDescription] = PropertyTypeDescriptionDropdown(formData.property_type_id);
  const [setIsDropdownOpenUseOfProperty,
    isDropdownOpenUseOfProperty,
    ,
    useOfProperty,
    loadingUseOfProperty] = UseOfPropertyDropdown(formData.property_type_id, formData.property_type_description_id);

  const [setIsDropdownOpenPurposeOfProperty,
    isDropdownOpenPurposeOfProperty,
    ,
    purposeOfProperty,
    loadingPurposeOfProperty] = PurposeOfPropertyDropdown(formData.property_type_id, formData.property_type_description_id, formData.use_of_property_id);

  const handlePropertyTypeSelect = (selectedValue: any) => {
    setFormData((prevState) => ({
      ...prevState,
      property_type_id: selectedValue,
    }));
    setIsDropdownOpen(false);
  };

  const selectedPropertyTypeName = propertyTypes.find((property: any) => property.type_primary_id === formData.property_type_id)?.type_name;

  const dropdownRefPropertyType = useRef<any>(null);
  const dropdownRefPropertyDescription = useRef<any>(null);
  const dropdownRefUseOfProperty = useRef<any>(null);
  const dropdownRefPurposeOfProperty = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRefPropertyType.current && !dropdownRefPropertyType.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }

      if (dropdownRefPropertyDescription.current && !dropdownRefPropertyDescription.current.contains(event.target as Node)) {
        setIsDropdownOpenPropertyDescription(false);
      }

      if (dropdownRefPurposeOfProperty.current && !dropdownRefPurposeOfProperty.current.contains(event.target as Node)) {
        setIsDropdownOpenPurposeOfProperty(false);
      }

      if (dropdownRefUseOfProperty.current && !dropdownRefUseOfProperty.current.contains(event.target as Node)) {
        setIsDropdownOpenUseOfProperty(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handlePropertyTypeDescriptionSelect = (e: any) => {
    const { value, checked } = e.target;

    setFormData((prevFormData: any) => {
      let newSelections = formData.property_type_description_id ? formData.property_type_description_id.split(",") : [];

      if (checked) {
        newSelections.push(value);
      } else {
        const index = newSelections.indexOf(value);
        if (index > -1) {
          newSelections.splice(index, 1);
        }
      }

      return {
        ...prevFormData,
        property_type_description_id: newSelections.join(","),
      };
    });
  };

  const selectedPropertyDescriptions = propertyDescriptions
    .filter((property: any) => formData.property_type_description_id.split(",").includes(String(property.id)))
    .map((property: any) => property.type_description_name)
    .join(", ");

  const selectedUseOfProperty = useOfProperty
    .find((property: any) => property.useproperty_primary_id == formData.use_of_property_id)
    ?.use_of_property;

  const purposeOfPropertyDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpenUseOfProperty((prev: any) => !prev);
  };

  const handlePropertyTypeSelectUseOfProperty = (selectedValue: number) => {
    setFormData((prevState) => ({
      ...prevState,
      use_of_property_id: selectedValue,
    }));
    setIsDropdownOpenUseOfProperty(false);
  };

  const selectedPurposeOfProperty = purposeOfProperty
    .find((property: any) => property.pourpose_primary_id == formData.purpose_of_use_id)
    ?.perpose_of_use;

  const PurposeOfPropertyToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpenPurposeOfProperty((prev: any) => !prev);
  };

  const handlePurposeOfPropertyTypeSelect = (selectedValue: any) => {
    setFormData((prevState) => ({
      ...prevState,
      purpose_of_use_id: selectedValue,
    }));
    setIsDropdownOpenPurposeOfProperty(false);
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Add Property Description</h1>
        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">

          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
              <div className="relative z-[4]">
                <label htmlFor="property_type_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Property Type <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefPropertyType}>
                  <button
                    onClick={handleDropdownToggle}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {selectedPropertyTypeName || "Select Property Type"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpen && (
                    <select
                      id="property_type_id"
                      name="property_type_id"
                      value={formData.property_type_id}
                      onChange={(e) => handlePropertyTypeSelect(Number(e.target.value))}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 absolute top-full"
                      size={10}
                    >
                      <option value="" disabled>Select Property Type</option>
                      {propertyTypes.map((property: any) => (
                        <option key={property.type_primary_id} value={property.type_primary_id}>
                          {property.type_name}
                        </option>
                      ))}
                      {loading && <option>Loading...</option>}
                    </select>
                  )}
                </div>
                {errors.property_type_id && <p className="text-sm text-red-600 mt-1">{errors.property_type_id}</p>}
              </div>

              <div className="relative z-[3]">
                <label htmlFor="property_description_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Property Type Description <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefPropertyDescription}>
                  <button
                    onClick={handleDropdownTogglePropertyDescription}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {selectedPropertyDescriptions || "Select Property Type Description"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenPropertyDescription && (
                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600">
                      {propertyDescriptions.length === 0 ? (
                        <p className="text-sm text-red-600 dark:text-white"> No Matching Property Type Description Found!</p>
                      ) : (
                        propertyDescriptions.map((property: any) => (
                          <div key={property.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`property_${property.id}`}
                              value={property.id}
                              checked={formData.property_type_description_id.split(",").includes(String(property.id))}
                              onChange={handlePropertyTypeDescriptionSelect}
                              className="text-blue-600"
                            />
                            <label htmlFor={`property_${property.id}`} className="text-sm text-gray-900 dark:text-white">
                              {property.type_description_name}
                            </label>
                          </div>
                        ))
                      )}
                      {loadingPropertyDescription && <p>Loading...</p>}
                    </div>
                  )}
                </div>
                {errors.property_type_description_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.property_type_description_id}</p>
                )}
              </div>

              <div className="relative z-[1]">
                <label htmlFor="use_of_property_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Use Of The Property <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefUseOfProperty}>
                  <button
                    onClick={purposeOfPropertyDropdownToggle}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {selectedUseOfProperty || "Select Use Of Property"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenUseOfProperty && (
                    <div className="absolute top-full w-full bg-white border border-gray-300 rounded-lg mt-1 z-10">
                      {useOfProperty.length === 0 ? (
                        <p className="text-sm text-red-600 p-2">No Matching Use Of Property Found!</p>
                      ) : (
                        <select
                          id="use_of_property_id"
                          name="use_of_property_id"
                          value={formData.use_of_property_id}
                          onChange={(e) => handlePropertyTypeSelectUseOfProperty(Number(e.target.value))}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          size={10}
                        >
                          <option value="" disabled>Select Use Of Property</option>
                          {useOfProperty.map((property: any) => (
                            <option key={property.useproperty_primary_id} value={property.useproperty_primary_id}>
                              {property.use_of_property}
                            </option>
                          ))}
                          {loadingUseOfProperty && <option>Loading...</option>}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                {errors.use_of_property_id && <p className="text-sm text-red-600 mt-1">{errors.use_of_property_id}</p>}
              </div>

              <div className="relative z-[1]">
                <label htmlFor="purpose_of_use_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Purpose Of Property <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefPurposeOfProperty}>
                  <button
                    onClick={PurposeOfPropertyToggle}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {selectedPurposeOfProperty || "Select Purpose Of Property"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenPurposeOfProperty && (
                    <div className="absolute top-full w-full bg-white border border-gray-300 rounded-lg mt-1 z-10">
                      {purposeOfProperty.length === 0 ? (
                        <p className="text-sm text-red-600 p-2">No Matching Purpose Of Property Found!</p>
                      ) : (
                        <select
                          id="purpose_of_use_id"
                          name="purpose_of_use_id"
                          value={formData.purpose_of_use_id}
                          onChange={(e) => handlePurposeOfPropertyTypeSelect(e.target.value)}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          size={10}
                        >
                          <option value="" disabled>Select Purpose Of Property</option>
                          {purposeOfProperty.map((property: any) => (
                            <option key={property.pourpose_primary_id} value={property.pourpose_primary_id}>
                              {property.perpose_of_use}
                            </option>
                          ))}
                          {loadingPurposeOfProperty && <option>Loading...</option>}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                {errors.purpose_of_use_id && (
                  <p className="text-sm text-red-600 mt-1">{errors.purpose_of_use_id}</p>
                )}
              </div>

              <div className="col-span-2">
                <label
                  htmlFor="description_name"
                  className="block text-sm mb-2 font-medium text-gray-900 dark:text-white"
                >
                  Property Description <span className="text-red-600">*</span>
                </label>
                <div className="flex items-end gap-4">
                  <div className="w-full flex flex-col gap-y-2 relative">
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag, index) => (
                        <div
                          key={index}
                          className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full flex items-center gap-1"
                        >
                          {tag}
                          <button
                            onClick={() => removeTag(tag)}
                            className="text-red-500 text-sm"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>

                    <input
                      type="text"
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyPress}
                      onBlur={handleBlur}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder="Type a word and press Enter"
                    />
                    {errors.description_name && (
                      <p className="text-sm text-red-600 absolute -bottom-4">{errors.description_name}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>

          {successMessage && (
            <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

          {dataExistMessage && (
            <div className="mt-4 p-4 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
              <span>{dataExistMessage}</span>
              <button
                onClick={() => setDataExistMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

          {dataFailedMessage && (
            <div className="mt-4 p-4 bg-red-600 text-white rounded-lg flex justify-between items-center">
              <span>{dataFailedMessage}</span>
              <button
                onClick={() => setDataFailedMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddPropertyDescription;